import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheckCircle, FiLock, FiCreditCard } = FiIcons;

const CheckoutModal = ({ isOpen, onClose }) => {
  const { cart, processOrder } = useData();
  const [step, setStep] = useState('form'); // 'form', 'processing', 'success'
  const [formData, setFormData] = useState({ name: '', email: '', card: '', expiry: '', cvv: '' });

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    setStep('processing');
    setTimeout(() => {
      processOrder();
      setStep('success');
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {step === 'form' && (
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Checkout</h2>
              <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-xl mb-6 flex justify-between items-center">
              <span className="text-blue-800 font-medium">Order Total</span>
              <span className="text-xl font-bold text-blue-900">${total.toFixed(2)}</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <input
                  required
                  placeholder="Full Name"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
                <input
                  required
                  type="email"
                  placeholder="Email Address"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              <div className="relative">
                <div className="absolute left-3 top-3.5 text-gray-400">
                  <SafeIcon icon={FiCreditCard} />
                </div>
                <input
                  required
                  placeholder="Card Number"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.card}
                  onChange={(e) => setFormData({...formData, card: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input
                  required
                  placeholder="MM/YY"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.expiry}
                  onChange={(e) => setFormData({...formData, expiry: e.target.value})}
                />
                <input
                  required
                  placeholder="CVV"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.cvv}
                  onChange={(e) => setFormData({...formData, cvv: e.target.value})}
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold flex items-center justify-center space-x-2 hover:bg-black transition-all"
              >
                <SafeIcon icon={FiLock} className="h-4 w-4" />
                <span>Pay Now</span>
              </button>
              <p className="text-center text-xs text-gray-400">Secured with 256-bit SSL Encryption</p>
            </form>
          </div>
        )}

        {step === 'processing' && (
          <div className="p-16 flex flex-col items-center justify-center text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full mb-6"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-500">Please do not refresh the page...</p>
          </div>
        )}

        {step === 'success' && (
          <div className="p-16 flex flex-col items-center justify-center text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6"
            >
              <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-green-600" />
            </motion.div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h2>
            <p className="text-gray-500 mb-8">Thank you for your purchase. We've sent a confirmation email to {formData.email}</p>
            <button
              onClick={onClose}
              className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CheckoutModal;